import {
  BulkCreateAttendanceReviewsDocument,
  BulkDestroyAttendanceReviewsDocument,
  TimesheetReviewStatsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'

interface TrackingData extends Record<string, string | number | undefined> {
  period_type: string
  start_on: string
  end_on: string
  employee_id: number
}

interface UsePeriodApprovalProps {
  employeeId: number
}

export const usePeriodApproval = ({ employeeId }: UsePeriodApprovalProps) => {
  const { stringDateFrom: startOn, stringDateTo: endOn, cycle } = useAttendanceDateNavigator()

  const payload = {
    employeeIds: [employeeId],
    startOn,
    endOn,
  }

  const trackingData: TrackingData = {
    period_type: cycle,
    start_on: startOn,
    end_on: endOn,
    employee_id: employeeId,
  }
  const [bulkCreateAttendanceReviews, { loading: bulkCreateLoading }] = useMutation(
    BulkCreateAttendanceReviewsDocument,
    {
      extractorFn: (data) => data.attendanceMutations.bulkCreateAttendanceReview,
      refetchQueries: [TimesheetReviewStatsDocument],
    }
  )

  const [bulkDestroyAttendanceReviews, { loading: bulkDeleteLoading }] = useMutation(
    BulkDestroyAttendanceReviewsDocument,
    {
      extractorFn: (data) => data.attendanceMutations.bulkDestroyAttendanceReview,
      refetchQueries: [TimesheetReviewStatsDocument],
    }
  )

  const approvePeriod = async () => {
    const result = await bulkCreateAttendanceReviews({ variables: payload })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      track(trackingEvents.timeTracking.approvePeriodFromTimesheet, trackingData)
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }

  const disapprovePeriod = async () => {
    const result = await bulkDestroyAttendanceReviews({ variables: payload })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      track(trackingEvents.timeTracking.disapprovePeriodFromTimesheet, trackingData)
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }

  return {
    approvePeriod,
    disapprovePeriod,
    loading: bulkCreateLoading || bulkDeleteLoading,
  }
}
